import React, { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import API_BASE_URL from '../../ApiConfig/ApiConfig';
import Cookies from "js-cookie";

const ProductRepairDetails = () => {
  const [repairDetails, setRepairDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRepairDetails = async () => {
      try {
        const token = Cookies.get('token');
        if (token) {
          const decodedToken = jwtDecode(token);
          const customerId = decodedToken.customerId;

          const response = await axios.get(`${API_BASE_URL}/ProductRepair/customer/${customerId}`, {
            headers: {
              "Content-Type": "application/json",
              "Authorization": token ? `Bearer ${token}` : "",
            }
          });

          if (response.data.length === 0) {
            setRepairDetails([]);
          } else {
            const sortedRepairDetails = response.data.sort((a, b) => {
              const aLatestDate = a.statusUpdates[a.statusUpdates.length - 1]?.date || 0;
              const bLatestDate = b.statusUpdates[b.statusUpdates.length - 1]?.date || 0;
              return new Date(bLatestDate) - new Date(aLatestDate);
            });

            setRepairDetails(sortedRepairDetails);
          }
        } else {
          setRepairDetails([]);
        }
      } catch (err) {
        console.error('Error fetching repair details:', err);
        setRepairDetails([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRepairDetails();
  }, []);

  const makeLinksClickable = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a key={index} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const getStatusStyle = (status) => {
    let statusStyle = "bg-red-100 text-red-600";
    if (status === "Under Process") {
      statusStyle = "bg-blue-100 text-blue-600";
    } else if (status === "Completed") {
      statusStyle = "bg-green-100 text-green-600";
    } else if (status === "On Hold") {
      statusStyle = "bg-yellow-100 text-yellow-600";
    }
    return statusStyle;
  };

  if (loading) {
    return (
      <div className="min-w-full max-w-7xl mx-auto bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm sm:shadow-md overflow-hidden mt-4 sm:mt-8 lg:mt-20" 
           style={{ boxShadow: '0px 10px 60px 0px #E2ECF980' }}>
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="flex items-center justify-center h-32 sm:h-48">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-[#1C50A3]"></div>
            <span className="ml-3 text-gray-600 text-sm sm:text-base">Loading repair details...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-full max-w-7xl mx-auto bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm sm:shadow-md lg:shadow-lg overflow-hidden mt-4 sm:mt-8 lg:mt-20"
         style={{ boxShadow: '0px 10px 60px 0px #E2ECF980' }}>
      
      {/* Header Section */}
      <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-100">
        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-700 mb-1">
          Product Repair Details
        </h1>
        <p className="text-sm text-gray-500 hidden sm:block">
          Track the status of your product repairs and service requests
        </p>
      </div>

      {repairDetails.length === 0 ? (
        <div className="p-8 sm:p-12 lg:p-16 text-center">
          <div className="max-w-sm mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Repair Records Found</h3>
            <p className="text-gray-600 text-sm">You don't have any repair requests at the moment.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full bg-white border-collapse">
              <thead className="bg-[#1C50A3] text-white">
                <tr>
                  <th className="py-3 px-4 xl:px-6 border-b border-gray-300 font-semibold text-left text-sm">
                    Product Name
                  </th>
                  <th className="py-3 px-4 xl:px-6 border-b border-gray-300 font-semibold text-left text-sm">
                    Product ID
                  </th>
                  <th className="py-3 px-4 xl:px-6 border-b border-gray-300 font-semibold text-left text-sm">
                    Ticket Number
                  </th>
                  <th className="py-3 px-4 xl:px-6 border-b border-gray-300 font-semibold text-left text-sm">
                    Status Date
                  </th>
                  <th className="py-3 px-4 xl:px-6 border-b border-gray-300 font-semibold text-left text-sm">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {repairDetails.map((repair, index) => (
                  <tr key={repair._id || `repair-${index}`} 
                      className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                    <td className="py-3 px-4 xl:px-6 border-b border-gray-200 text-left text-gray-700 text-sm font-medium">
                      {repair.productDetails?.productName || 'N/A'}
                    </td>
                    <td className="py-3 px-4 xl:px-6 border-b border-gray-200 text-left text-gray-600 text-sm">
                      {repair.productId}
                    </td>
                    <td className="py-3 px-4 xl:px-6 border-b border-gray-200 text-left text-gray-600 text-sm">
                      {repair.ticketNumber}
                    </td>
                    <td className="py-3 px-4 xl:px-6 border-b border-gray-200 text-left text-gray-600 text-sm">
                      {repair.statusUpdates[repair.statusUpdates.length - 1]?.date
                        ? new Date(repair.statusUpdates[repair.statusUpdates.length - 1]?.date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                        : 'N/A'}
                    </td>
                    <td className="py-3 px-4 xl:px-6 border-b border-gray-200 text-left">
                      {(() => {
                        const currentStatus = repair.statusUpdates[repair.statusUpdates.length - 1]?.status || "N/A";
                        const statusStyle = getStatusStyle(currentStatus);
                        return (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyle}`}>
                            {currentStatus}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tablet View - Card Layout */}
          <div className="hidden md:block lg:hidden">
            <div className="p-4 space-y-4">
              {repairDetails.map((repair, index) => (
                <div key={repair._id || `repair-${index}`}
                     className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">{repair.productDetails?.productName || 'N/A'}</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Product ID:</span>
                          <span className="text-gray-700">{repair.productId}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Ticket:</span>
                          <span className="text-gray-700">{repair.ticketNumber}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="mb-2 text-sm text-gray-600">
                        {repair.statusUpdates[repair.statusUpdates.length - 1]?.date
                          ? new Date(repair.statusUpdates[repair.statusUpdates.length - 1]?.date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                          : 'N/A'}
                      </div>
                      {(() => {
                        const currentStatus = repair.statusUpdates[repair.statusUpdates.length - 1]?.status || "N/A";
                        const statusStyle = getStatusStyle(currentStatus);
                        return (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyle}`}>
                            {currentStatus}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile View - Stacked Cards */}
          <div className="md:hidden">
            <div className="p-3 space-y-3">
              {repairDetails.map((repair, index) => (
                <div key={repair._id || `repair-${index}`}
                     className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-base mb-2 leading-tight">
                        {repair.productDetails?.productName || 'N/A'}
                      </h3>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-600 font-medium">Product ID:</span>
                        <span className="text-gray-800 text-right">{repair.productId}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-600 font-medium">Ticket Number:</span>
                        <span className="text-gray-800 text-right">{repair.ticketNumber}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-600 font-medium">Status Date:</span>
                        <span className="text-gray-800 text-right">
                          {repair.statusUpdates[repair.statusUpdates.length - 1]?.date
                            ? new Date(repair.statusUpdates[repair.statusUpdates.length - 1]?.date).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-100 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium text-sm">Status:</span>
                        {(() => {
                          const currentStatus = repair.statusUpdates[repair.statusUpdates.length - 1]?.status || "N/A";
                          const statusStyle = getStatusStyle(currentStatus);
                          return (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyle}`}>
                              {currentStatus}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductRepairDetails;
